/**
 * Services catalog screen (DNKADM-0079) — the consolidated **Services** destination
 * that replaces the two former nav items "Services" and "Service categories". A MUI
 * sub-tab container (mirrors `MembershipCreditsScreen`) hosting two tabs routed under
 * `/services`:
 *  - **Services** (default, first) — the grouped-by-category service catalog
 *    ({@link ServicesScreen}), at the index path `/services`.
 *  - **Service categories** — the existing category CRUD
 *    (`ServiceCategoriesScreen`), at `/services/categories`.
 *
 * The active tab is derived from the URL and each tab navigates to its path, so the
 * tabs are deep-linkable and back/forward works. The tab bodies render through an
 * `<Outlet />`. The `/services/:serviceId` service-detail route is a sibling (NOT a
 * tab) and is untouched; the fixed `categories` child segment out-ranks the
 * `:serviceId` param so the two never collide. Owner/Admin only (the route is wrapped
 * in `RequireAdmin`; this screen does not re-gate).
 */
import { Box, Tab, Tabs } from '@mui/material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { COURT_GREEN } from '@/theme';

/** The two tabs in display/route order. `path` is absolute (index = `/services`). */
const TABS = [
  { path: '/services', label: 'Services' },
  { path: '/services/categories', label: 'Service categories' },
] as const;

type TabPath = (typeof TABS)[number]['path'];

/**
 * Resolve the active tab from the current URL: anything under `/services/categories`
 * is the categories tab; the index path `/services` (and any other in-container path)
 * is the Services tab. The container only renders under `/services` and
 * `/services/categories` — the `:serviceId` detail is a sibling route.
 */
function activeTabFrom(pathname: string): TabPath {
  if (pathname === '/services/categories' || pathname.startsWith('/services/categories/')) {
    return '/services/categories';
  }
  return '/services';
}

export function ServicesCatalogScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const active = activeTabFrom(location.pathname);

  return (
    <Box>
      <Tabs
        value={active}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        onChange={(_e, value: TabPath) => navigate(value)}
        aria-label="Services sections"
        sx={{
          mb: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTabs-indicator': { bgcolor: COURT_GREEN },
          '& .Mui-selected': { color: `${COURT_GREEN} !important` },
        }}
      >
        {TABS.map((t) => (
          <Tab key={t.path} value={t.path} label={t.label} />
        ))}
      </Tabs>

      <Outlet />
    </Box>
  );
}
